/* eslint-disable no-console */
import type { QueryClient } from '@tanstack/react-query';
import { ChunkAssembler } from './chunk-assembler.ts';
import { decompressData } from './decompress.ts';
import type { ChannelMapping } from './websocket.types.ts';
import { useWebSocketStore } from '@/services/store/use-websocket-store.ts';

const WS_TARGET = import.meta.env.VITE_WEBSOCKET_URL;

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;

const normalizeWebSocketUrl = (url: string): string => {
  if (url.startsWith('wss://') || url.startsWith('ws://')) {
    return url;
  }

  const isSecure = window.location.protocol === 'https:';
  const protocol = isSecure ? 'wss://' : 'ws://';

  return `${protocol}${url}`;
};

const getWebSocketUrl = (): string | null => {
  if (import.meta.env.DEV) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws`;
  }

  if (!WS_TARGET) {
    return null;
  }

  return normalizeWebSocketUrl(WS_TARGET);
};

const calculateReconnectDelay = (attempts: number): number => {
  const delay = BASE_RECONNECT_DELAY * Math.pow(2, attempts);
  const jittered = delay * (1 + Math.random() * 0.3);
  return Math.min(jittered, MAX_RECONNECT_DELAY);
};

class WebSocketService {
  private ws: WebSocket | null = null;
  private queryClient: QueryClient | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pendingSubscriptions: string[] = [];
  private intentionalDisconnect = false;
  private chunkAssembler = new ChunkAssembler();

  private get store() {
    return useWebSocketStore.getState();
  }

  setQueryClient(client: QueryClient) {
    this.queryClient = client;
  }

  connect() {
    this.clearReconnectTimeout();
    this.store.resetReconnectAttempts();
    this.connectInternal();
  }

  disconnect() {
    this.intentionalDisconnect = true;
    this.clearReconnectTimeout();
    this.ws?.close();
    this.ws = null;
    this.chunkAssembler.clear();
    this.store.reset();
  }

  subscribe(channels: string[]) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: 'subscribe', channels }));
    } else {
      this.pendingSubscriptions = [...new Set([...this.pendingSubscriptions, ...channels])];
      if (this.store.status === 'disconnected') {
        this.connect();
      }
    }
  }

  unsubscribe(channels: string[]) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: 'unsubscribe', channels }));
    }
    this.pendingSubscriptions = this.pendingSubscriptions.filter(
      (ch) => !channels.includes(ch)
    );
  }

  send(message: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const data = typeof message === 'string' ? message : JSON.stringify(message);
      this.ws.send(data);
    }
  }

  // --- Private ---

  private connectInternal() {
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING)
      return;

    const wsUrl = getWebSocketUrl();
    if (!wsUrl) return;

    this.intentionalDisconnect = false;
    this.store.setStatus('connecting');
    this.store.setError(null);

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      this.store.setStatus('connected');
      this.store.resetReconnectAttempts();

      if (this.pendingSubscriptions.length > 0) {
        const channels = this.pendingSubscriptions;
        this.pendingSubscriptions = [];
        ws.send(JSON.stringify({ action: 'subscribe', channels }));
      }
    };

    ws.onmessage = (event: MessageEvent) => {
      this.handleMessage(event);
    };

    ws.onclose = () => {
      this.ws = null;
      this.store.setStatus('disconnected');
      this.chunkAssembler.clear();

      if (!this.intentionalDisconnect) {
        // Read subscriptions from store (not from closure) to fix stale reference bug
        const { subscriptions } = useWebSocketStore.getState();
        this.pendingSubscriptions = [...subscriptions];
        this.scheduleReconnect();
      }
    };

    ws.onerror = () => {
      this.store.setError(new Error('WebSocket connection error'));
    };

    this.ws = ws;
  }

  private handleMessage(event: MessageEvent) {
    const rawData = typeof event.data === 'string' ? event.data : String(event.data);
    const { rawMessageListeners } = useWebSocketStore.getState();

    // 1. Fire raw listeners
    for (const listener of rawMessageListeners) {
      listener(rawData);
    }

    // 2. Parse JSON
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(rawData);
    } catch {
      console.warn('[WebSocketService] Failed to parse message:', rawData.slice(0, 100));
      return;
    }

    // 3. System messages (subscribed / unsubscribed)
    if (
      (parsed.action === 'subscribed' || parsed.action === 'unsubscribed') &&
      Array.isArray(parsed.subscriptions)
    ) {
      this.store.setSubscriptions(parsed.subscriptions as string[]);
      return;
    }

    // 4. Chunked message
    if (parsed.type === 'chunk') {
      const completeJson = this.chunkAssembler.handleChunk(
        parsed as unknown as {
          chunkId: string;
          index: number;
          total: number;
          data: string;
        }
      );
      if (completeJson) {
        try {
          const reassembled = JSON.parse(completeJson) as Record<string, unknown>;
          this.dispatchParsedMessage(reassembled, completeJson);
        } catch {
          console.warn('[WebSocketService] Failed to parse reassembled chunk');
        }
      }
      return;
    }

    // 5. Dispatch the parsed message
    this.dispatchParsedMessage(parsed, rawData);
  }

  private dispatchParsedMessage(parsed: Record<string, unknown>, rawData: string) {
    const { messageListeners, channelMappings } = useWebSocketStore.getState();

    // Compressed message → decompress first
    if (typeof parsed.compressed === 'string' && typeof parsed.channel === 'string') {
      try {
        const decompressed = decompressData(parsed.compressed);
        this.dispatchToChannel(
          parsed.channel,
          decompressed,
          JSON.stringify(decompressed),
          messageListeners,
          channelMappings
        );
      } catch (err) {
        console.warn('[WebSocketService] Decompression failed:', err);
      }
      return;
    }

    // Action messages (error, etc.)
    if (typeof parsed.action === 'string') {
      const actionListeners = messageListeners.get('__action__');
      if (actionListeners) {
        for (const listener of actionListeners) {
          listener(parsed);
        }
      }
      return;
    }

    // Channel data messages
    if (typeof parsed.channel === 'string' && parsed.data !== undefined) {
      this.dispatchToChannel(
        parsed.channel,
        parsed.data,
        rawData,
        messageListeners,
        channelMappings
      );
    }
  }

  private dispatchToChannel(
    channel: string,
    data: unknown,
    rawData: string,
    messageListeners: Map<string, Set<(data: unknown) => void>>,
    channelMappings: Map<string, ChannelMapping[]>
  ) {
    const payload = { channel, data, rawData };

    // Fire channel-specific message listeners
    const listeners = messageListeners.get(channel);
    if (listeners) {
      for (const listener of listeners) {
        listener(payload);
      }
    }

    // Fire wildcard listeners (channel = '*')
    const wildcardListeners = messageListeners.get('*');
    if (wildcardListeners) {
      for (const listener of wildcardListeners) {
        listener(payload);
      }
    }

    // Apply React Query channel mappings
    const mappings = channelMappings.get(channel);
    if (mappings && this.queryClient) {
      for (const mapping of mappings) {
        const transformedData = mapping.transform ? mapping.transform(data) : data;

        if (mapping.updateStrategy === 'invalidate') {
          this.queryClient.invalidateQueries({ queryKey: mapping.queryKey });
        } else if (mapping.updateStrategy === 'setData') {
          this.queryClient.setQueryData(mapping.queryKey, transformedData);
        }
      }
    }
  }

  private scheduleReconnect() {
    if (this.intentionalDisconnect) return;

    const { reconnectAttempts } = useWebSocketStore.getState();
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      this.store.setError(new Error('Max reconnection attempts reached'));
      return;
    }

    const delay = calculateReconnectDelay(reconnectAttempts);
    this.clearReconnectTimeout();

    this.reconnectTimeout = setTimeout(() => {
      this.store.incrementReconnectAttempts();
      this.connectInternal();
    }, delay);
  }

  private clearReconnectTimeout() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
}

export const wsService = new WebSocketService();
